// Professional feature access (SEN-6027 / SEN-6028).
// Contract-based professional access stored as a `ProfessionalAccess` node on a
// user's Pro `Organization`. Granted / revoked through the admin-service
// `ProfessionalAccessGrant` / `ProfessionalAccessRevoke` mutations and read by
// traversing `user -> organization -> activeProfessionalAccess`.

export type ProFeature = 'PRO_BOOST' | 'PRO_ASSESSMENTS' | 'PRO_TRAIN';
export type ProAccessType = 'UNLIMITED' | 'FIXED';

export const PRO_FEATURES: { value: ProFeature; label: string }[] = [
  { value: 'PRO_BOOST', label: 'Pro Boost' },
  { value: 'PRO_ASSESSMENTS', label: 'Pro Assessments' },
  { value: 'PRO_TRAIN', label: 'Pro Train' }
];

// DateTime fields are _Neo4jDateTime on the graph and are selected as
// { formatted } (an ISO string), matching every other date in these queries. An
// omitted expiry is represented by an epoch sentinel rather than null.
export interface FormattedDate {
  formatted: string;
}

export interface ProfessionalAccessData {
  id: string;
  features: ProFeature[];
  assessmentAccessType?: ProAccessType;
  totalAssessments?: number;
  inProgressAssessments?: number;
  usedAssessments?: number;
  availableAssessments?: number;
  headsetLimit?: number;
  assignedDate?: FormattedDate;
  expiryDate?: FormattedDate;
  created?: FormattedDate;
  grantsLinkedUserMembershipTrialDays?: number;
}

export type OrgType = 'CLINIC_OR_HUB' | 'INDIVIDUAL' | 'OTHER';

export const ORG_TYPES: { value: OrgType; label: string }[] = [
  { value: 'CLINIC_OR_HUB', label: 'Clinic or Hub' },
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'OTHER', label: 'Other' }
];

export const orgTypeLabel = (type?: OrgType): string =>
  ORG_TYPES.find((t) => t.value === type)?.label ?? type ?? '—';

export interface OrganizationData {
  id: string;
  organizationName?: string;
  organizationType?: OrgType;
  organizationTypeOther?: string;
  activeProfessionalAccess?: ProfessionalAccessData[];
  expiredProfessionalAccess?: ProfessionalAccessData[];
}

export const proFeatureLabel = (feature: ProFeature): string =>
  PRO_FEATURES.find((f) => f.value === feature)?.label ?? feature;

export const hasProFeature = (
  access: ProfessionalAccessData,
  feature: ProFeature
): boolean => (access.features ?? []).includes(feature);

export const isFixedAccess = (access: ProfessionalAccessData): boolean =>
  access.assessmentAccessType === 'FIXED';

// UNLIMITED access is unlimited assessments capped by a headset count; FIXED is
// a fixed batch of assessments. Describe each by what it actually limits.
export const assessmentAccessTypeLabel = (type?: ProAccessType): string => {
  if (type === 'FIXED') return 'Fixed number of assessments';
  if (type === 'UNLIMITED') return 'Limited number of headsets';
  return '—';
};

// An omitted expiry is stored as an epoch sentinel (1970), so treat any date at
// or before the epoch year as "no expiry".
export const parseFormattedDate = (value?: FormattedDate): Date | undefined => {
  if (!value?.formatted) return undefined;
  const date = new Date(value.formatted);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

export const accessExpiryDate = (
  access: ProfessionalAccessData
): Date | undefined => {
  const date = parseFormattedDate(access.expiryDate);
  // An omitted expiry is stored as an epoch sentinel (1970) → treat as no expiry.
  if (!date || date.getUTCFullYear() <= 1970) return undefined;
  return date;
};
